import { cloneElement, forwardRef, isValidElement, useId } from 'react'
import { Search, X } from 'lucide-react'
import { Select, SearchSelect } from './Picker.jsx'
import { DateRangePicker } from './DateRangePicker.jsx'

/**
 * Form fields. Dropdowns (Select / SearchSelect) live in Picker.jsx — the kit never
 * uses the browser's native <select> menu.
 *
 * Audit: 49 local FIELD/INPUT class constants in ~11 variants
 * (13 / 13.5 / 14 / 15px, rounded-lg vs xl), labels in 4 styles, and no
 * required-field marker anywhere but one dialog.
 *
 * <Field label required hint error> wraps any control and draws:
 *   label above · red * when required · grey hint below · red error below (replaces the hint).
 * The control inside gets the matching id and invalid styling automatically when
 * it is a kit Input / Select / Textarea passed as the only child — otherwise pass
 * `id` yourself.
 */
export const FIELD_CLASS =
  'w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-[13.5px] text-slate-800 placeholder:text-slate-400 ' +
  'focus:outline-none focus:border-brand-400 focus:ring-4 focus:ring-brand-100 ' +
  'disabled:bg-slate-50 disabled:text-slate-500 disabled:cursor-not-allowed'
const INVALID = ' border-rose-300 focus:border-rose-400 focus:ring-rose-100'

export function Field({ label, required = false, hint, error, htmlFor, className = '', children }) {
  const auto = useId()
  const id = htmlFor || auto
  return (
    <div className={className}>
      {label && (
        <label htmlFor={id} className="block text-[12.5px] font-medium text-slate-600 mb-1">
          {label}{required && <span className="text-rose-500 ml-0.5" aria-hidden>*</span>}
        </label>
      )}
      {typeof children === 'function'
        ? children({ id, invalid: !!error })
        : isValidElement(children) && KIT_CONTROLS.has(children.type)
          ? cloneElement(children, { id: children.props.id || id, invalid: children.props.invalid ?? !!error })
          : children}
      {error
        ? <p className="mt-1 text-[12.5px] text-rose-600">{error}</p>
        : hint ? <p className="mt-1 text-[12px] text-slate-400">{hint}</p> : null}
    </div>
  )
}

export const Input = forwardRef(function Input({ invalid = false, className = '', ...rest }, ref) {
  return <input ref={ref} className={`${FIELD_CLASS}${invalid ? INVALID : ''} ${className}`} aria-invalid={invalid || undefined} {...rest} />
})

export const Textarea = forwardRef(function Textarea({ invalid = false, rows = 3, className = '', ...rest }, ref) {
  return <textarea ref={ref} rows={rows} className={`${FIELD_CLASS} leading-relaxed${invalid ? INVALID : ''} ${className}`} aria-invalid={invalid || undefined} {...rest} />
})

// The controls <Field> wires up by itself (id for the label, invalid from `error`).
const KIT_CONTROLS = new Set([Input, Select, SearchSelect, DateRangePicker, Textarea])

/**
 * Search box. Audit: 36 search boxes, icon offset in 4 positions, some with a
 * clear ✕ and some without. Always has the ✕ once there is text.
 * `scope` says what it searches when it is NOT the whole list — e.g. 'หน้านี้'.
 */
export function SearchInput({ value, onChange, placeholder = 'ค้นหา…', scope, autoFocus, className = '' }) {
  const ph = scope ? `${placeholder.replace(/…$/, '')}ใน${scope}…` : placeholder
  return (
    <div className={`relative ${className}`}>
      <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
      <input
        type="search"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={ph}
        autoFocus={autoFocus}
        className={`${FIELD_CLASS} pl-9 pr-8 [&::-webkit-search-cancel-button]:hidden`}
      />
      {value && (
        <button type="button" onClick={() => onChange('')} title="ล้างคำค้น" aria-label="ล้างคำค้น"
          className="absolute right-2 top-1/2 -translate-y-1/2 w-6 h-6 rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-100 flex items-center justify-center">
          <X size={14} />
        </button>
      )}
    </div>
  )
}
